import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Minus, AlertCircle, CheckCircle, Info } from 'lucide-react';
import api from '../../api'
import 'react-toastify/dist/ReactToastify.css';

export default function CreateOrganization() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({
    nomOrganisation: '',
    description: '',
    adresse: '',
    telephone: '',
    domaines: [''],
    date_debut_collaboration: '',
    module_ids: [],
    prix_jour: 0,
  });

  // Récup modules existants
  useEffect(() => {
    api.get('/modules')
      .then(r => setModules(r.data))
      .catch(() => toast.error('Erreur de chargement des modules'));
  }, []);

  // Validation
  const validations = {
    nomOrganisation: value => {
      if (!value) return 'Ce champ est obligatoire';
      if (value.length < 3) return 'Minimum 3 caractères';
      return null;
    },
    telephone: value => {
      if (!value) return 'Ce champ est obligatoire';
      const phoneRegex = /^0[5-7][0-9]{8}$/;
      if (!phoneRegex.test(value)) return 'Numéro de téléphone marocain invalide';
      return null;
    },
    date_debut_collaboration: v => !v ? 'Date requise' : new Date(v) > new Date() ? 'Ne peut pas être dans le futur' : null,
    module_ids: arr => arr.length === 0 ? 'Sélectionnez au moins un module' : null,
    prix_jour: v => !v || v <= 0 ? 'Prix journalier requis' : null,
    domaines: arr => {
      if (!arr || arr.length === 0) return 'Au moins un domaine est requis';
      for (let d of arr) {
        if (!d || d.trim() === '') return 'Les domaines ne doivent pas être vides';
        const domainRegex = /^[a-z0-9.-]+\.[a-z]{2,}$/i;
        if (!domainRegex.test(d.trim())) return `Domaine invalide: ${d}`;
      }
      return null;
    },
  };

  // Gère tous les changements
  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    // Checkbox modules
    if (type === 'checkbox') {
      const id = parseInt(value, 10);
      setForm(f => ({
        ...f,
        module_ids: checked
          ? [...f.module_ids, id]
          : f.module_ids.filter(x => x !== id)
      }));
      setErrors(e => ({ ...e, module_ids: null }));
      return;
    }

    // Domaines dynamiques (name === "domaines[0]", etc.)
    const m = name.match(/^domaines\[(\d+)\]$/);
    if (m) {
      const idx = +m[1];
      setForm(f => {
        const arr = [...f.domaines];
        arr[idx] = value;
        return { ...f, domaines: arr };
      });
      setErrors(e => ({ ...e, domaines: null }));
      return;
    }

    // Champs classiques
    setForm(f => ({ ...f, [name]: value }));
    setErrors(e => ({ ...e, [name]: null }));
  };

  // Affiche la validation d'un champ
  const handleBlur = e => {
    const { name, value } = e.target;
    let error = null;
    if (name.startsWith('domaines')) {
      error = validations.domaines(form.domaines);
    } else {
      error = validations[name]?.(value) ?? null;
    }
    setErrors(e => ({ ...e, [name.startsWith('domaines') ? 'domaines' : name]: error }));
  };

  // Soumission
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    // Valide tous les champs
    const newErrors = {};
    let valid = true;
    for (let key of Object.keys(validations)) {
      const val = key === 'domaines'
        ? form.domaines
        : form[key];
      const err = validations[key](val);
      if (err) { newErrors[key] = err; valid = false; }
    }
    if (!valid) {
      setErrors(newErrors);
      toast.error('Corrigez les erreurs');
      setLoading(false);
      return;
    }

    // Envoi
    try {
      await api.post('/organisations', form);
      toast.success('Organisation créée', { autoClose: 500 });
      navigate('/admin/organisations');
    } catch (err) {
      const be = err.response?.data?.errors || {};
      const formatted = Object.entries(be).reduce((o,[k,v])=>{
        o[k] = Array.isArray(v) ? v[0] : v;
        return o;
      }, {});
      setErrors(formatted);
      toast.error(err.response?.data?.message || 'Erreur de création');
    } finally {
      setLoading(false);
    }
  };

  // Classes dynamiques
  const inputClasses = field =>
    `w-full px-4 py-3 rounded-lg border focus:ring-2 ${
      errors[field]
        ? 'border-red-500 focus:ring-red-200'
        : 'border-gray-300 focus:border-terrecuite focus:ring-terrecuite/20'
    }`;

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2">
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-8 flex items-center justify-center gap-2">
          <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-terrecuite" />
          Nouvelle Organisation
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Nom */}
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm md:text-base">Nom <span className="text-red-500">*</span></label>
            <input
              name="nomOrganisation"
              value={form.nomOrganisation}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClasses('nomOrganisation')}
              placeholder="Easy Systeme"
            />
            {errors.nomOrganisation && <p className="text-red-500 mt-1 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{errors.nomOrganisation}</p>}
          </div>

          {/* Téléphone */}
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm md:text-base">Téléphone <span className="text-red-500">*</span></label>
            <input
              type="tel"
              name="telephone"
              value={form.telephone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClasses('telephone')}
              placeholder="0612345678"
            />
            {errors.telephone && <p className="text-red-500 mt-1 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{errors.telephone}</p>}
          </div>

          {/* Domaines dynamiques */}
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm md:text-base">Domaines <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              {form.domaines.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    name={`domaines[${i}]`}
                    value={d}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={inputClasses('domaines')}
                    placeholder="example.ma"
                  />
                  {i === form.domaines.length - 1 ? (
                    <button 
                      type="button" 
                      onClick={() => setForm(f => ({ ...f, domaines: [...f.domaines, ''] }))} 
                      className="p-2 bg-green-100 hover:bg-green-200 rounded-full"
                      aria-label="Ajouter un domaine"
                    >
                      <Plus className="w-4 h-4 md:w-5 md:h-5 text-green-600"/>
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      onClick={() => setForm(f => ({ ...f, domaines: f.domaines.filter((_, idx) => idx !== i) }))} 
                      className="p-2 bg-red-100 hover:bg-red-200 rounded-full"
                      aria-label="Supprimer ce domaine"
                    >
                      <Minus className="w-4 h-4 md:w-5 md:h-5 text-red-600"/>
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.domaines && <p className="text-red-500 mt-1 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{errors.domaines}</p>}
          </div>

          {/* Adresse */}
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm md:text-base">Adresse</label>
            <input
              name="adresse"
              value={form.adresse}
              onChange={handleChange}
              className={inputClasses('adresse')}
              placeholder="123, Rue Example"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block mb-2 text-sm md:text-base">Date de début <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="date_debut_collaboration"
              value={form.date_debut_collaboration}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClasses('date_debut_collaboration')}
              max={new Date().toISOString().split('T')[0]}
            />
            {errors.date_debut_collaboration && <p className="text-red-500 mt-1 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{errors.date_debut_collaboration}</p>}
          </div>

          {/* Prix journalier */}
          <div>
            <label className="block mb-2 text-sm md:text-base">Prix journalier <span className="text-red-500">*</span></label>
            <input
              type="number"
              name="prix_jour"
              value={form.prix_jour}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClasses('prix_jour')}
            />
            {errors.prix_jour && <p className="text-red-500 mt-1 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{errors.prix_jour}</p>}
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm md:text-base font-medium mb-2 text-gray-700">
              Description
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              className={`${inputClasses('description')} min-h-[100px]`}
              placeholder="Décrivez l'organisation..."
            />
          </div>

          {/* Modules */}
          <div className="md:col-span-2">
            <label className="block mb-2 text-sm md:text-base">Modules <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 p-3 md:p-4 border rounded-lg">
              {modules.map(mod => (
                <label key={mod.id} className="flex items-center space-x-2 py-1">
                  <input
                    type="checkbox"
                    name="module_ids"
                    value={mod.id}
                    checked={form.module_ids.includes(mod.id)}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-terrecuite focus:ring-terrecuite"
                  />
                  <span className="text-sm md:text-base">{mod.nom}</span>
                </label>
              ))}
            </div>
            {errors.module_ids && <p className="text-red-500 mt-1 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4"/>{errors.module_ids}</p>}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 md:mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button 
            type="button" 
            onClick={() => navigate(-1)} 
            className="px-4 py-2 sm:px-6 sm:py-2 border rounded-lg hover:bg-gray-50 text-sm md:text-base"
          >
            Annuler
          </button>
          <button 
            type="submit" 
            disabled={loading} 
            className="px-4 py-2 sm:px-6 sm:py-2 bg-terrecuite text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 text-sm md:text-base mb-2 sm:mb-0"
          >
            {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
            {loading ? 'Création...' : 'Créer'}
          </button>
        </div>

        {/* Info */}
        <div className="mt-4 md:mt-6 p-3 md:p-4 bg-blue-50 rounded-lg flex items-start gap-3">
          <Info className="w-4 h-4 md:w-5 md:h-5 text-terrecuite flex-shrink-0 mt-0.5"/>
          <p className="text-xs md:text-sm text-terrecuite">
            Tous les champs marqués * sont obligatoires.
          </p>
        </div>
      </form>
    </div>
  );
}